import { useMsal } from '@azure/msal-react';
import { Client } from '@microsoft/microsoft-graph-client';
import microsoftlogo from 'assets/microsoftlogo.png';
import BaseModal from 'components/Modals/BaseModal';
import { PermsContextType } from 'contexts/perms.types';
import RolesTable from 'features/Admin/Roles/RolesTable/RolesTable';
import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ms_getSecurityGroups, tryMsAuthSilent } from 'services/auth/ms.service';
import { getPermissionsMeta } from 'services/perms/perms.service';
import { ms_getRoleLinks, ms_updateRoleLinks } from 'services/roles/microsoft/ms.service';
import { useCreateRoleMutation } from 'services/roles/roles.api';
import { MicrosoftSecurityGroup } from 'types/msGraph.types';
import { notifyErrorToast, notifySuccessToast } from 'utils/notifications/toast.utils';
import ImportMSRoles from '../../../pages/Admin/Components/ImportMSRoles';
import { NewRole } from './types/Roles.types';
import { normalizePermName } from './utils/Roles.utils';

interface RolesTabProps {
  perms: PermsContextType;
}

export default function RolesTab({ perms }: RolesTabProps) {
  const { instance } = useMsal();
  const navigate = useNavigate();

  const [showConfirmLinkRoles, setShowConfirmLinkRoles] = useState<boolean>(false);
  const [newRoles, setNewRoles] = useState<NewRole[]>([]);
  const [showImportRoles, setShowImportRoles] = useState(false);
  const [isMsalAuthenticated, setIsMsalAuthenticated] = useState(false);
  const [linkMulti, setLinkMulti] = useState<Record<string, number[]>>({});
  const [msGroups, setMsGroups] = useState<MicrosoftSecurityGroup[]>([]);
  const [createRole] = useCreateRoleMutation();

  useEffect(() => {}, [perms]);

  const onCreateRole = async () => {
    navigate('/system/createrole');
  };

  useEffect(() => {
    const fetchMSal = async () => {
      try {
        const msalauth = await tryMsAuthSilent(instance);

        setIsMsalAuthenticated(!!msalauth);
      } catch (error) {
        console.error('Error fetching msalAuth:', error);
        setIsMsalAuthenticated(false);
      }
    };
    fetchMSal();
  }, [instance]);

  const getMsInfo = async () => {
    // MS Graph API
    const scopes = ['Group.Read.All'];
    try {
      const msRes = await tryMsAuthSilent(instance, scopes);
      if (!msRes) {
        toast.error('Microsoft authentication not configured or no account is signed in.');
        return;
      }

      const client = Client.init({
        authProvider: (done: (err: Error | null, accessToken: string | null) => void) => {
          done(null, msRes.accessToken ?? null);
        },
      });

      let groups = await ms_getSecurityGroups(instance);

      setMsGroups(groups?.data?.result || []);

      // get links
      const res = await ms_getRoleLinks(instance);
      const links = res?.data?.result || {};

      const linkMultis: Record<string, number[]> = {};
      Object.keys(links).forEach((link) => {
        linkMultis[link] = links[link];
      });

      setLinkMulti(linkMultis);
    } catch (err) {
      console.error('getMsInfo failed', err);
      toast.error('Failed to fetch Microsoft groups. Check console for details.');
    }
  };

  const onImportRoles = async () => {
    setShowImportRoles(true);
    getMsInfo();
  };

  const sendImportRoles = async () => {
    let linkObj: Record<string, number[]> = { ...linkMulti };

    if (newRoles.length > 0) {
      let permData: Record<string, boolean> = {};

      const metaRes = await getPermissionsMeta();
      const apiPerms: { name: string }[] = metaRes?.data?.meta ?? [];
      permData = apiPerms.reduce((acc: Record<string, boolean>, p) => {
        acc[normalizePermName(p.name)] = false;
        return acc;
      }, {});
      for (const newRole of newRoles) {
        try {
          const newRoleId = await createRole({
            roleName: newRole.name,
            members: [],
            permissions: JSON.stringify(permData),
            layout: '[]',
          }).unwrap();
          linkObj = { ...linkObj, [newRole.id]: [newRoleId] };
          notifySuccessToast('Role', { isAddOperation: true });
        } catch (error) {
          console.error('Failed to create role for', newRole.name);
          notifyErrorToast('Role', { isAddOperation: true });
        }
      }
    }

    // NOTE: If someone calls this and Microsoft does not give up ALL of
    //      the MsGroup IDs, then this will break and unlink things that
    //      are valid.
    let finalLinks: Record<string, { roles: number[]; name: string }> = {};
    Object.keys(linkObj).forEach((group) => {
      const match = msGroups.find((find) => find.id === group);
      if (match)
        finalLinks = {
          ...finalLinks,
          [group]: {
            roles: linkObj[group],
            name: match.displayName,
          },
        };
    });

    interface SyncResult {
      type?: string;
      status?: string;
      user?: { email?: string };
    }

    let res: { data: { syncResults: SyncResult[] } } | undefined;
    try {
      res = await ms_updateRoleLinks(instance, finalLinks);
    } catch (err) {
      console.error('ms_updateRoleLinks failed', err);
      toast.error('Failed to sync role links with Microsoft. Check console for details.');
      return;
    }

    const syncResults: SyncResult[] = res?.data?.syncResults ?? [];
    const newUsers = syncResults.filter((r) => r.type === 'newUser');

    const affectedUsers = syncResults.filter((r) => r.status !== 'error');
    if (affectedUsers) {
      let failedUsers: string[] = [];
      let successUsers: string[] = [];
      let failedMessage = '';
      let successMessage = '';

      newUsers.forEach((user) => {
        if (user.status === 'error') failedUsers.push(user.user?.email ?? '');
        else successUsers.push(user.user?.email ?? '');
      });
      if (successUsers.length > 0) {
        successMessage = `Successfully imported ${successUsers.length} users.\n`;
      }
      if (failedUsers.length > 0) {
        failedMessage = `Failed to import ${failedUsers.length} users:\n${failedUsers.join('\n')}`;
      }

      let finalMessage = '';
      if (successMessage && failedMessage) finalMessage = successMessage + '\n' + failedMessage;
      else if (successMessage) finalMessage = successMessage;
      else if (failedMessage) finalMessage = failedMessage;

      if (finalMessage) toast.success(finalMessage);
      else toast.success('Sync updated.');
    }
    setShowImportRoles(false);
    setShowConfirmLinkRoles(false);
    setNewRoles([]);
  };

  return (
    <div className="w-100 d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
      <div className="d-flex w-100 justify-content-between">
        <div className="d-flex align-self-end bg-primary rounded-3">
          <div
            style={{
              height: 39,
              borderRadius: 10,
            }}
          ></div>
        </div>
      </div>

      <RolesTable perms={perms} msalInstance={instance} />
      <div className="d-flex justify-content-between mt-3">
        {perms.testPermission([8, 11, 12, 13]) && isMsalAuthenticated && (
          <Button
            className="d-flex align-items-center justify-content-center p-1 bg-primary rounded"
            style={{
              height: 65,
            }}
            onClick={onImportRoles}
          >
            <img src={microsoftlogo} style={{ height: 20 }} alt="Microsoft" />
            <div className="ms-2">Link Security Groups</div>
          </Button>
        )}
        {showImportRoles && (
          <BaseModal
            show={showImportRoles}
            handleShow={() => setShowImportRoles(false)}
            title={
              <div className="flex-row gap-2">
                <img src={microsoftlogo} style={{ height: 30 }} alt="Microsoft" />
                <div>Link Security Groups </div>
              </div>
            }
          >
            <ImportMSRoles
              msGroups={msGroups}
              linkMulti={linkMulti}
              setLinkMulti={setLinkMulti}
              showConfirmLinkRoles={showConfirmLinkRoles}
              setShowConfirmLinkRoles={setShowConfirmLinkRoles}
              newRoles={newRoles}
              setNewRoles={setNewRoles}
              sendImportRoles={sendImportRoles}
            />
          </BaseModal>
        )}
        {perms.testPermission([8, 9, 10, 11, 12, 13], ['CreateRole']) && (
          <Button
            className="d-flex align-items-center justify-content-center bg-primary rounded"
            style={{
              height: 65,
            }}
            onClick={onCreateRole}
          >
            <div>Create Role</div>
          </Button>
        )}
      </div>
    </div>
  );
};


